import React from 'react';

import 'semantic-ui-css/semantic.min.css';
import { Box, Divider, List, ListItem, Menu } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

import OhsLoginServices from 'login/OhsLoginServices';
import { OhsMultiUserLoginOption } from 'login/multi/OhsMultiUserLoginOption';
import OhsComponentLoading from 'global-services/utils/OhsLoading';
import { unMorphUser } from 'user/morph/MorphServices';
import OhsLogoutModal from 'login/OhsLogoutModal';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

export default function OhsSwitchWorkplaceMenu(props: {
  anchorEl: Element;
  setAnchorEl: React.Dispatch<React.SetStateAction<Element | null>>;
  loginOptions: OhsMultiUserLoginOption[];
  isLoadingOptions: boolean;
}) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [logoutModalOpen, setLogoutModalOpen] = React.useState(false);
  const [userOptions, setUserOptions]: [OhsMultiUserLoginOption[] | undefined, any] =
    React.useState();

  const menuId = 'primary-search-account-menu';
  const isMenuOpen = Boolean(props.anchorEl);
  const user = OhsUserLocalServices.getLocalOhsUser();
  const navigate = useNavigate();

  const handleDialogClose = async (item: OhsMultiUserLoginOption) => {
    setIsLoading(true);
    if (user?.isMorph) await unMorphUser();

    const response: any = await OhsLoginServices.multiUserLogin(
      item.user._id,
      item.sessionToken ?? ''
    );
    setIsLoading(false);
    if (_.has(response, '_id')) {
      const homeURL = OhsLoginServices.getHomeUrl(response.tier);
      navigate(homeURL);
      navigate(0);
    }
  };

  React.useEffect(() => {
    setIsLoading(props.isLoadingOptions);
  }, [props.isLoadingOptions]);

  React.useEffect(() => {
    if (props.loginOptions.length > 0) setUserOptions(props.loginOptions);
  }, [props.loginOptions]);

  return (
    <>
      <Menu
        anchorEl={props.anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        id={menuId}
        keepMounted
        open={isMenuOpen}
        onClose={() => {
          props.setAnchorEl(null);
          setUserOptions(undefined);
        }}
      >
        <Box width={400}>
          <Box px={6}>
            <Box py={4}>
              <Typography variant="h4" fontWeight="bold">
                Log out or switch
              </Typography>
              <Typography style={{ color: 'black' }}>
                Pick a user to switch to, or select Log Out below
              </Typography>
            </Box>
          </Box>
          {isLoading && <OhsComponentLoading />}
          <List>
            {!isLoading &&
              userOptions
                ?.sort((x, y) => {
                  if (x.blocked === y.blocked) {
                    return 0;
                  }
                  return x.blocked ? 1 : -1;
                })
                .sort((x, y) => {
                  if (x.inProgress === y.inProgress) {
                    return 0;
                  }
                  return x.inProgress ? 1 : -1;
                })
                .map((item, index) => (
                  <div key={index}>
                    <Divider />
                    <ListItem
                      button
                      onClick={() => handleDialogClose(item)}
                      disabled={item.blocked === true || item.inProgress === true}
                      style={
                        item.user._id === user?._id
                          ? { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
                          : {}
                      }
                    >
                      <Box px={4} py={2}>
                        {user?._id === item.user._id && (
                          <Box pb={1}>
                            <Typography color="textSecondary">
                              YOU&apos;RE CURRENTLY LOGGED IN AS:
                            </Typography>
                          </Box>
                        )}
                        {(() => {
                          if (item.blocked) {
                            return (
                              <Typography style={{ fontWeight: 'bold', color: 'red' }}>
                                Limited User Access
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          if (item.inProgress) {
                            return (
                              <Typography style={{ fontWeight: 'bold', color: 'red' }}>
                                In Progress
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          if (item.user.tier.type === 'core.tier.T2') {
                            return (
                              <Typography style={{ fontWeight: 'bold' }}>
                                {item.orgAdminTier.name} (Org Admin)
                              </Typography>
                            );
                          }

                          if (item.user.tier.type === 'core.tier.T3') {
                            return (
                              <Typography style={{ fontWeight: 'bold' }}>
                                {item.orgTier.name} (Organisation)
                              </Typography>
                            );
                          }

                          if (item.user.tier.type === 'core.tier.T4') {
                            return (
                              <Typography style={{ fontWeight: 'bold' }}>
                                {item.workplaceTier?.name} (Workplace)
                              </Typography>
                            );
                          }

                          if (item.user.tier.type === 'core.tier.T5') {
                            return (
                              <Typography style={{ fontWeight: 'bold' }}>
                                {item.workerGroupTier?.name} (Worker Group)
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                        <Typography style={{ fontWeight: 'bold' }}>
                          Profile: {item.user.profile.title}
                        </Typography>
                        {(() => {
                          if (item.user.tier.type === 'core.tier.T4') {
                            return (
                              <Typography color="textSecondary">
                                {`${item.orgTier.name}`}
                              </Typography>
                            );
                          }

                          if (item.user.tier.type === 'core.tier.T5') {
                            return (
                              <Typography color="textSecondary">
                                {`${item?.workplaceTier?.name} < ${item.orgTier.name}`}
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </ListItem>
                  </div>
                ))}
          </List>
          <Divider />
          <ListItem
            button
            onClick={() => setLogoutModalOpen(true)}
            style={{ textAlign: 'center', justifyContent: 'center' }}
          >
            <Box display="flex" px={4} py={2}>
              <Typography
                variant="h4"
                color="textSecondary"
                style={{
                  textDecoration: 'underline',
                }}
              >
                LOG OUT
              </Typography>
            </Box>
          </ListItem>
        </Box>
      </Menu>
      <OhsLogoutModal open={logoutModalOpen} setLogoutModalOpen={setLogoutModalOpen} />
    </>
  );
}
